import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardHeader from '../Header/DashboardHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faCalendarAlt, faHeading, faPaperPlane, faTimes } from '@fortawesome/free-solid-svg-icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './UpdateBlogs.css';

function UpdateBlogs() {
  const navigate = useNavigate();
  const { id } = useParams(); // Get the blog ID from URL params
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    image: null,
    date: getCurrentDate(),
    content: '',
    id: id
  });
  
  // Preview state
  const [imagePreview, setImagePreview] = useState(null);
  const [originalImagePath, setOriginalImagePath] = useState('');
  
  // Check if admin is logged in
  useEffect(() => {
    const isLoggedIn = sessionStorage.getItem('admin-login');
    if (!isLoggedIn) {
      // Redirect to home page if not logged in
      navigate('/');
    } else {
      setIsAuthorized(true);
    }
  }, [navigate]);
  
  // Fetch blog data when component mounts
  useEffect(() => {
    if (isAuthorized && id) {
      fetchBlogData();
    }
  }, [isAuthorized, id]);
  
  // Function to fetch blog data
  const fetchBlogData = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`http://localhost/blog/backend/Api/View.php?id=${id}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.status === 'success') {
        // Format date to YYYY-MM-DD for input field
        const formattedDate = formatDateForInput(data.data.date_published);
        
        setFormData({
          title: data.data.title || '',
          content: data.data.content || '',
          date: formattedDate,
          id: id
        });
        
        if (data.data.image_path) {
          setImagePreview(data.data.image_path);
          setOriginalImagePath(data.data.image_path);
        }
        
        setError(null);
      } else {
        setError(data.message || 'Failed to fetch blog data');
        toast.error(data.message || 'Failed to fetch blog data');
      }
    } catch (err) {
      console.error('Error fetching blog data:', err);
      setError('Failed to fetch blog data. Please try again later.');
      toast.error('Failed to fetch blog data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Get current date in YYYY-MM-DD format
  function getCurrentDate() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  // Format date from database to YYYY-MM-DD for input field
  function formatDateForInput(dateString) {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle content change from Quill editor
  const handleContentChange = (content) => {
    setFormData({
      ...formData,
      content: content
    });
  };
  
  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        image: file
      });
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Trigger file input click
  const triggerFileInput = () => {
    fileInputRef.current.click();
  };
  
  // Remove selected image
  const removeImage = () => {
    setFormData({
      ...formData,
      image: null
    });
    setImagePreview(null);
    fileInputRef.current.value = '';
  };

  // Quill editor modules and formats
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link', 'image', 'video'],
      ['clean']
    ],
  };
  
  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'align',
    'link', 'image', 'video'
  ];

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (isSubmitting) return;
    
    try {
      setIsSubmitting(true);
      
      // Create FormData object for file upload
      const blogData = new FormData();
      blogData.append('id', formData.id);
      blogData.append('title', formData.title);
      blogData.append('date', formData.date);
      blogData.append('content', formData.content);
      
      if (formData.image) {
        blogData.append('image', formData.image);
      }
      
      // Generate excerpt from content (first 150 characters without HTML tags)
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = formData.content;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      const excerpt = textContent.substring(0, 150) + (textContent.length > 150 ? '...' : '');
      blogData.append('excerpt', excerpt);
      
      // Send data to backend
      const response = await fetch('https://b.centrexmarineservices.com/Api/Update.php', {
        method: 'POST',
        body: blogData,
      });
      
      const result = await response.json();
      
      if (response.ok && result.status === 'success') {
        // Show success toast
        toast.success('Blog post updated successfully!');
        
        // Redirect after a short delay
        setTimeout(() => {
          navigate('/dashboard/view-blogs');
        }, 2000);
      } else {
        throw new Error(result.message || 'Failed to update blog post');
      }
    } catch (error) {
      console.error('Error updating blog:', error);
      // Show error toast
      toast.error(error.message || 'Failed to update blog post. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel button click
  const handleCancel = () => {
    // Prevent navigation if already submitting
    if (!isSubmitting) {
      navigate('/dashboard/view-blogs');
    }
  };

  // Show loading state while fetching blog data
  if (isLoading && isAuthorized) {
    return (
      <div className="update-blogs-page">
        <DashboardHeader />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading blog data...</p>
        </div>
        <ToastContainer position="top-right" autoClose={5000} />
      </div>
    );
  }

  // Show error message if there was an error fetching blog data
  if (error && isAuthorized) {
    return (
      <div className="update-blogs-page">
        <DashboardHeader />
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button 
            className="back-button"
            onClick={() => navigate('/dashboard/view-blogs')}
          >
            Back to Blogs
          </button>
        </div>
        <ToastContainer position="top-right" autoClose={5000} />
      </div>
    );
  }

  // Only render content if user is authorized
  if (!isAuthorized) {
    return null; // Return nothing while checking authorization or redirecting
  }

  return (
    <div className="create-blogs-page">
      <DashboardHeader />
      
      <div className="create-blogs-container">
        <div className="page-header">
          <h1>Update Blog Post</h1>
          <p>Edit the details below to update this blog post</p>
        </div>
        
        <form onSubmit={handleSubmit} className="create-blogs-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="title">
                <FontAwesomeIcon icon={faHeading} className="form-icon" />
                Blog Title
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter blog title"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="date">
                <FontAwesomeIcon icon={faCalendarAlt} className="form-icon" />
                Publication Date
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="image">
              <FontAwesomeIcon icon={faImage} className="form-icon" />
              Blog Image
            </label>
            <div className="image-upload-container">
              <input
                type="file"
                id="image"
                name="image"
                ref={fileInputRef}
                onChange={handleImageChange}
                accept="image/*"
                className="hidden-file-input"
              />
              
              {!imagePreview ? (
                <div className="image-upload-placeholder" onClick={triggerFileInput}>
                  <FontAwesomeIcon icon={faImage} className="upload-icon" />
                  <p>Click to select an image</p>
                  <span>Recommended size: 1200 x 800 pixels</span>
                </div>
              ) : (
                <div className="image-preview-container">
                  <img src={imagePreview} alt="Preview" className="image-preview" />
                  <button 
                    type="button" 
                    className="remove-image-btn"
                    onClick={removeImage}
                    disabled={isSubmitting}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              )}
            </div>
          </div>
          
          <div className="form-group">
            <label>Blog Content</label>
            <div className="quill-editor-container">
              <ReactQuill
                theme="snow"
                value={formData.content}
                onChange={handleContentChange}
                modules={modules}
                formats={formats}
                placeholder="Write your blog content here..."
              />
            </div>
          </div>
          
          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-btn" 
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              <FontAwesomeIcon icon={faTimes} className="btn-icon" />
              Cancel
            </button>
            <button 
              type="submit" 
              className="submit-btn"
              disabled={isSubmitting}
            >
              <FontAwesomeIcon icon={faPaperPlane} className="btn-icon" />
              {isSubmitting ? 'Updating...' : 'Update Blog Post'}
            </button>
          </div>
        </form>
      </div>
      
      {/* Toast container for notifications */}
      <ToastContainer 
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}

export default UpdateBlogs;
